import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  Button,
  IconButton,
  Popover,
  Chip,
} from '@mui/material';
import {
  Send,
  Person,
  School,
  AdminPanelSettings,
  EmojiEmotions,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../services/api';

interface Conversation {
  id: number;
  conversation_id?: number; // For backward compatibility
  title: string;
  created_at: string;
  other_user_name: string;
  other_user_role: string;
  last_message?: string;
  last_message_time?: string;
}

interface Message {
  id: number;
  message: string;
  full_name: string;
  role: string;
  created_at: string;
}

interface User {
  id: number;
  full_name: string;
  username: string;
  role: string;
}

const Chat: React.FC = () => {
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const EMOJIS = [
    '😀','😁','😂','🤣','😊','😍','😘','😎','🙂','🤗','🤔','😐','🙄','😴','🤤','😷','🤒','🤕','🤢','🤮',
    '🤧','🥳','🤯','😬','😇','😜','🤪','😤','😭','😡','👍','👎','👏','🙏','👌','🤝','💪','🔥','✨','💯',
    '🎉','🥰','😺','😹','😻','🙈','🙉','🙊','🫡','🫶','❤️','💙','💚','💛','💜','🖤'
  ];

  const { data: conversations = [] } = useQuery<Conversation[]>('chat-conversations', async () => {
    const response = await api.get('/chat/conversations');
    return response.data.conversations;
  });

  const { data: messages = [] } = useQuery<Message[]>(
    ['chat-messages', selectedConversation?.id],
    async () => {
      if (!selectedConversation) return [];
      const response = await api.get(`/chat/conversations/${selectedConversation.id}/messages`);
      return response.data.messages;
    },
    {
      enabled: !!selectedConversation,
      refetchInterval: 3000, // Poll every 3 seconds
    }
  );

  const { data: availableUsers = [] } = useQuery<User[]>('chat-users', async () => {
    const response = await api.get('/chat/users');
    return response.data.users;
  });

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return availableUsers;
    return availableUsers.filter((u) =>
      u.full_name.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q)
    );
  }, [availableUsers, searchQuery]);

  const sendMessageMutation = useMutation(
    async (message: string) => {
      await api.post(`/chat/conversations/${selectedConversation!.id}/messages`, { message });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chat-messages', selectedConversation?.id]);
        setNewMessage('');
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to send message', { variant: 'error' });
      },
    }
  );

  const createConversationMutation = useMutation(
    async (participant: User) => {
      const title = `Chat: ${user?.full_name} & ${participant.full_name}`;
      const response = await api.post('/chat/conversations', {
        title,
        participant_ids: [participant.id],
      });
      return { conversationId: response.data.conversationId as number, participant, title };
    },
    {
      onSuccess: async ({ conversationId, participant, title }) => {
        try {
          await queryClient.invalidateQueries('chat-conversations');
        } catch {}
        setSelectedConversation({
          id: conversationId,
          title,
          created_at: new Date().toISOString(),
          other_user_name: participant.full_name,
          other_user_role: participant.role,
        } as any);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to start conversation', { variant: 'error' });
      },
    }
  );

  // removed: createConversationMutation and new conversation flow

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (newMessage.trim() && selectedConversation) {
      sendMessageMutation.mutate(newMessage);
    }
  };

  const toggleEmojiPicker = (e: React.MouseEvent<HTMLElement>) => {
    setEmojiAnchorEl(emojiAnchorEl ? null : e.currentTarget);
  };

  const onPickEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
  };

  // removed: handleCreateConversation since creating new conversations is disabled

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminPanelSettings />;
      case 'supervisor':
        return <School />;
      case 'student':
        return <Person />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'supervisor':
        return 'warning';
      case 'student':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Chat</Typography>
      </Box>

      <Grid container spacing={3} sx={{ height: 'calc(100vh - 140px)', alignItems: 'stretch', minHeight: 0 }}>
        {/* Available Users List (left) */}
        <Grid item xs={12} md={4} sx={{ height: '100%', minHeight: 0 }}>
          <Paper sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
              <TextField
                fullWidth
                placeholder="Search users by name, username, or role"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
              />
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }} className="custom-scrollbar">
            <List>
              {filteredUsers.map((u) => (
                <ListItem
                  key={u.id}
                  button
                  onClick={() => {
                    // Find existing conversation with this user if present
                    const existing = conversations.find(c => (c as any).other_user_id === u.id);
                    if (existing) {
                      setSelectedConversation(existing);
                    } else {
                      createConversationMutation.mutate(u);
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      {getRoleIcon(u.role)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={u.full_name}
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {u.username}
                      </Typography>
                    }
                  />
                  <Chip
                    label={u.role}
                    color={getRoleColor(u.role) as any}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
            </Box>
          </Paper>
        </Grid>

        {/* Messages / Conversations (right) */}
        <Grid item xs={12} md={8} sx={{ height: '100%', minHeight: 0 }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedConversation.other_user_name} • {selectedConversation.other_user_role}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    30 days the chat will auto delete
                  </Typography>
                </Box>

                {/* Messages */}
                <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', p: 2 }} className="custom-scrollbar">
                  {messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: message.full_name === user?.full_name ? 'flex-end' : 'flex-start',
                        mb: 2,
                      }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          maxWidth: '70%',
                          backgroundColor: message.full_name === user?.full_name ? 'primary.main' : 'background.paper',
                          color: message.full_name === user?.full_name ? 'white' : 'text.primary',
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="body2" sx={{ mb: 1, color: message.full_name === user?.full_name ? 'white' : 'text.secondary' }}>
                          {message.full_name}
                        </Typography>
                        <Typography variant="body1" sx={{ color: message.full_name === user?.full_name ? 'white' : 'text.primary' }}>
                          {message.message}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          opacity: 0.7,
                          color: message.full_name === user?.full_name ? 'white' : 'text.secondary'
                        }}>
                          {new Date(message.created_at).toLocaleTimeString()}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton aria-label="emoji" onClick={toggleEmojiPicker} size="large">
                      <EmojiEmotions />
                    </IconButton>
                    <TextField
                      fullWidth
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                    >
                      <Send />
                    </Button>
                  </Box>
                  <Popover
                    open={Boolean(emojiAnchorEl)}
                    anchorEl={emojiAnchorEl}
                    onClose={() => setEmojiAnchorEl(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 1, maxWidth: 260 }}>
                      <Grid container spacing={1}>
                        {EMOJIS.map((e) => (
                          <Grid item key={e}>
                            <Button onClick={() => onPickEmoji(e)} size="small" sx={{ minWidth: 32, p: 0 }}>
                              {e}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Popover>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="h6" color="text.secondary">
                  Select a conversation or pick a user on the left to start chatting
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* removed: New Conversation Dialog */}
    </Box>
  );
};

export default Chat; 